/**
 * Scoring Form Print Page
 *
 * Print-optimized page for offline evaluation forms.
 * Opens in new tab and triggers print dialog automatically.
 */

import { useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useEvaluation, useCalculateScores } from './useEvaluations';
import { useEmployeeAssignedTasks } from '../employees/useEmployeeTasks';
import { competenciesApi, type EmployeeCompetencies } from '../../lib/competenciesApi';
import { useQuery } from '@tanstack/react-query';
import type { EvaluationStatus } from '../../lib/evaluationsApi';
import { useScoreCriteria, useAgreementTypes } from '../settings/useSettings';
import { PackageSnapshotView } from './PackageSnapshotView';
import type { ScoreCriteriaDTO, AgreementTypeDTO, PackageSnapshot } from '@functieloonhuis/shared';

const STATUS_LABELS: Record<EvaluationStatus, string> = {
  draft: 'Concept',
  submitted: 'Ingediend',
  in_review: 'In Review',
  final: 'Definitief',
  cancelled: 'Geannuleerd',
};

const SCORE_LABELS: Record<number, string> = {
  1: 'Onvoldoende',
  2: 'Matig',
  3: 'Voldoende',
  4: 'Goed',
  5: 'Uitzonderlijk',
};

// Compact inline criteria block for print
function InlineCriteriaBlock({ criteria }: { criteria: ScoreCriteriaDTO[] }) {
  if (!criteria || criteria.length === 0) return null;

  return (
    <div className="criteria-block mt-2 mb-3 bg-gray-50 border border-gray-200 rounded p-2 text-xs">
      <p className="font-medium text-gray-700 mb-1.5">Score criteria:</p>
      <div className="space-y-0.5">
        {criteria.map((c) => (
          <div key={c.scoreLevel} className="flex gap-2">
            <span className="font-bold text-gray-600 w-4 flex-shrink-0">{c.scoreLevel}</span>
            <span className="text-gray-500 w-20 flex-shrink-0">{c.label}:</span>
            <span className="text-gray-600">{c.description}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Context-aware criteria block that fetches specific criteria for task/competency
// Falls back to general criteria if no specific criteria exist
function ContextAwareCriteriaBlock({
  contextType,
  contextId,
  generalCriteria,
}: {
  contextType: 'task' | 'competency';
  contextId: number;
  generalCriteria: ScoreCriteriaDTO[];
}) {
  const { data: specificCriteria } = useScoreCriteria(contextType, contextId);

  // Use specific criteria if available, otherwise fall back to general
  const criteriaToUse = specificCriteria && specificCriteria.length > 0
    ? specificCriteria
    : generalCriteria;

  if (!criteriaToUse || criteriaToUse.length === 0) return null;

  const sortedCriteria = [...criteriaToUse].sort((a, b) => a.scoreLevel - b.scoreLevel);

  return <InlineCriteriaBlock criteria={sortedCriteria} />;
}

// Agreement type checkboxes for print (can be checked with pen)
function AgreementTypeCheckboxes({
  selectedTypeIds,
  agreementTypes,
  showOnlyIfLowScore,
  score,
}: {
  selectedTypeIds: number[];
  agreementTypes: AgreementTypeDTO[];
  showOnlyIfLowScore?: boolean;
  score: number | null;
}) {
  // If showOnlyIfLowScore is true, only show for scores 1-2
  if (showOnlyIfLowScore && (score === null || score > 2)) {
    return null;
  }

  const isRequired = score !== null && score <= 2;
  const activeTypes = agreementTypes.filter((t) => t.isActive);

  if (activeTypes.length === 0) return null;

  return (
    <div className="agreement-checkboxes mt-3 mb-2">
      <span className="text-sm text-gray-600 mr-2">
        Afspraaktype{isRequired && <span className="text-red-500">*</span>}:
      </span>
      <div className="inline-flex flex-wrap gap-3 mt-1">
        {activeTypes.map((type) => {
          const isSelected = selectedTypeIds.includes(type.id);
          return (
            <label key={type.id} className="flex items-center gap-1.5 text-sm">
              <span
                className={`
                  agreement-checkbox w-4 h-4 border-2 rounded flex items-center justify-center flex-shrink-0
                  ${isSelected ? 'filled border-blue-600 bg-blue-600' : 'border-gray-400'}
                `}
              >
                {isSelected && (
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </span>
              <span className={isSelected ? 'text-gray-900' : 'text-gray-600'}>
                {type.displayName}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

// Agreement description lines for print (4 lines like toelichting)
// Always visible so users can write agreements for any score
function AgreementDescriptionLines({
  existingDescription,
}: {
  existingDescription?: string;
}) {
  return (
    <div className="agreement-description mt-3 mb-3">
      <span className="text-sm text-gray-600">
        Afspraak beschrijving:
      </span>
      <div className="mt-2 space-y-6">
        {existingDescription ? (
          <p className="text-sm text-gray-800 italic border-b border-gray-300 pb-1">{existingDescription}</p>
        ) : (
          <div className="border-b border-gray-300"></div>
        )}
        <div className="border-b border-gray-300"></div>
        <div className="border-b border-gray-300"></div>
        <div className="border-b border-gray-300"></div>
      </div>
    </div>
  );
}

export function ScoringFormPrintPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const evaluationId = Number(id);

  const { data: evaluation, isLoading: isLoadingEval, error } = useEvaluation(evaluationId);
  useCalculateScores(evaluationId); // Pre-fetch for potential future use
  const { data: assignedTasks, isLoading: isLoadingTasks } = useEmployeeAssignedTasks(
    evaluation?.employeeId || 0
  );
  const { data: employeeCompetencies, isLoading: isLoadingComps } = useQuery<EmployeeCompetencies>({
    queryKey: ['competencies', 'employee', evaluation?.employeeId],
    queryFn: () => competenciesApi.getForEmployee(evaluation?.employeeId || 0),
    enabled: !!evaluation?.employeeId,
  });

  // Epic 4 (Story 4.4, AC #3): een afgeronde core_tasks_v2-beoordeling print
  // uitsluitend vanuit het package_snapshot — geen live join met de
  // kerntakencatalogus (zelfde grondbeginsel als StepKerntaken's
  // final-branch, zie PackageSnapshotView).
  const isFinalV2Snapshot = evaluation?.status === 'final' && evaluation?.scoringModel === 'core_tasks_v2' && Boolean(evaluation?.packageSnapshot);
  const packageSnapshot: PackageSnapshot | null = useMemo(() => {
    if (!evaluation?.packageSnapshot) return null;
    try {
      return JSON.parse(evaluation.packageSnapshot) as PackageSnapshot;
    } catch {
      return null;
    }
  }, [evaluation?.packageSnapshot]);

  // Fetch score criteria for inline display
  const { data: scoreCriteria, isLoading: isLoadingCriteria } = useScoreCriteria('general');

  // Fetch agreement types for checkboxes
  const { data: agreementTypes, isLoading: isLoadingAgreementTypes } = useAgreementTypes();

  const isLoading = isLoadingEval || isLoadingTasks || isLoadingComps || isLoadingCriteria || isLoadingAgreementTypes;

  // Helper to get criteria sorted by score level
  const getSortedCriteria = (): ScoreCriteriaDTO[] => {
    if (!scoreCriteria) return [];
    return [...scoreCriteria].sort((a, b) => a.scoreLevel - b.scoreLevel);
  };

  // Auto-trigger print dialog when data is loaded
  useEffect(() => {
    if (!isLoading && evaluation) {
      // Small delay to ensure rendering is complete
      const timer = setTimeout(() => {
        window.print();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isLoading, evaluation]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-xl font-bold text-red-600">Fout</h1>
          <p className="mt-2 text-gray-600">Beoordeling niet gevonden</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Terug
          </button>
        </div>
      </div>
    );
  }

  if (isLoading || !evaluation) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Scoringsformulier voorbereiden...</p>
        </div>
      </div>
    );
  }

  const tasks = assignedTasks?.tasks || [];
  const basisCompetencies = employeeCompetencies?.basis || [];
  const vakCompetencies = employeeCompetencies?.vak || [];

  // Get existing scores if any
  const existingTaskScores = evaluation.taskScores || [];
  const existingCompScores = evaluation.competencyScores || [];

  const formatPeriod = (start: string, end: string) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    return `${startDate.toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' })} - ${endDate.toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' })}`;
  };

  // Calculate points per task
  const TASK_WEIGHT = 50;
  const taskCount = tasks.length;
  const pointsPerTask = taskCount > 0 ? TASK_WEIGHT / taskCount : 0;

  // Calculate points per competency
  const VAK_WEIGHT = 30;
  const BASIS_WEIGHT = 20;
  const vakCount = vakCompetencies.length;
  const basisCount = basisCompetencies.length;
  const pointsPerVak = vakCount > 0 ? VAK_WEIGHT / vakCount : 0;
  const pointsPerBasis = basisCount > 0 ? BASIS_WEIGHT / basisCount : 0;

  return (
    <>
      {/* Print Styles */}
      <style>{`
        @media print {
          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .no-print { display: none !important; }
          .print-page {
            padding: 0 !important;
            margin: 0 !important;
          }
          .score-circle {
            border: 2px solid #333 !important;
            -webkit-print-color-adjust: exact !important;
          }
          .score-circle.filled {
            background-color: #2563eb !important;
            color: white !important;
          }
          /* Prevent cards from being split across pages */
          .print-card {
            break-inside: avoid;
            page-break-inside: avoid;
          }
          /* Inline criteria block print styling */
          .criteria-block {
            background-color: #f9fafb !important;
            border: 1px solid #e5e7eb !important;
            -webkit-print-color-adjust: exact !important;
          }
          /* Keep section headers with their content */
          .print-section-header {
            break-after: avoid;
            page-break-after: avoid;
          }
          /* Allow page breaks between sections */
          .print-section {
            break-before: auto;
            page-break-before: auto;
          }
          /* Agreement type checkboxes print styling */
          .agreement-checkbox {
            border: 2px solid #9ca3af !important;
            -webkit-print-color-adjust: exact !important;
          }
          .agreement-checkbox.filled {
            background-color: #2563eb !important;
            border-color: #2563eb !important;
          }
          .agreement-checkboxes {
            -webkit-print-color-adjust: exact !important;
          }
        }
        @page {
          size: A4;
          margin: 15mm;
        }
        /* Hide browser default headers and footers */
        @page {
          margin-top: 15mm;
          margin-bottom: 15mm;
          margin-left: 15mm;
          margin-right: 15mm;
        }
        @page :first {
          margin-top: 15mm;
        }
      `}</style>

      {/* Print Button (hidden when printing) */}
      <div className="no-print fixed top-4 right-4 flex gap-2 z-50">
        <button
          onClick={() => window.print()}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 shadow-lg flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
          </svg>
          Afdrukken / PDF opslaan
        </button>
        <button
          onClick={() => window.close()}
          className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 shadow-lg"
        >
          Sluiten
        </button>
      </div>

      {/* Print Content */}
      <div className="print-page bg-white min-h-screen p-8 max-w-4xl mx-auto">
        {/* Header */}
        <div className="border-b-2 border-blue-600 pb-4 mb-6">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-bold text-blue-800">Scoringsformulier</h1>
              <p className="text-gray-600 mt-1">{formatPeriod(evaluation.periodeStart, evaluation.periodeEnd)}</p>
            </div>
            <div className="text-right">
              <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                evaluation.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                evaluation.status === 'final' ? 'bg-green-100 text-green-800' :
                'bg-blue-100 text-blue-800'
              }`}>
                {STATUS_LABELS[evaluation.status]}
              </span>
              <p className="text-xs text-gray-500 mt-2">
                Afgedrukt: {new Date().toLocaleDateString('nl-NL')}
              </p>
            </div>
          </div>
        </div>

        {/* Employee Info */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Medewerker:</span>
              <span className="ml-2 font-medium">
                {evaluation.employee?.name || `Medewerker ${evaluation.employee?.userId || 'Onbekend'}`}
              </span>
            </div>
            <div>
              <span className="text-gray-500">Functie:</span>
              <span className="ml-2 font-medium">{evaluation.employee?.functie || '-'}</span>
            </div>
            <div>
              <span className="text-gray-500">Team:</span>
              <span className="ml-2 font-medium">{evaluation.employee?.team || '-'}</span>
            </div>
            <div>
              <span className="text-gray-500">Beoordelaar:</span>
              <span className="ml-2 font-medium">
                {evaluation.managerName || `Manager #${evaluation.managerId}`}
              </span>
            </div>
          </div>
        </div>

        {/* Score Legend */}
        <div className="bg-blue-50 rounded-lg p-3 mb-6">
          <p className="text-xs font-medium text-blue-800 mb-2">Scorelegenda:</p>
          <div className="flex gap-4 text-xs">
            {[1, 2, 3, 4, 5].map((score) => (
              <span key={score} className="flex items-center gap-1">
                <span className="w-5 h-5 rounded-full border-2 border-gray-400 flex items-center justify-center text-xs font-bold">
                  {score}
                </span>
                <span className="text-gray-600">{SCORE_LABELS[score]}</span>
              </span>
            ))}
          </div>
        </div>

        {/* Tasks Section — v2 (core_tasks_v2, afgeronde beoordeling): print
            uitsluitend het pakket-snapshot (AC #3, AD-5), nooit het
            v1-live-taken-formulier hieronder. */}
        {isFinalV2Snapshot && packageSnapshot ? (
          <div className="mb-8 print-section" data-testid="print-package-snapshot">
            <div className="flex items-center justify-between mb-3 print-section-header">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <span className="w-6 h-6 rounded bg-blue-600 text-white text-xs flex items-center justify-center">1</span>
                Kerntakenbeoordeling
              </h2>
              <span className="text-sm text-gray-500">Max {TASK_WEIGHT} punten</span>
            </div>
            <PackageSnapshotView snapshot={packageSnapshot} finalizedAt={evaluation?.finalizedAt ?? null} />
          </div>
        ) : (
        <div className="mb-8 print-section">
          <div className="flex items-center justify-between mb-3 print-section-header">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <span className="w-6 h-6 rounded bg-blue-600 text-white text-xs flex items-center justify-center">1</span>
              Takenbeoordeling
            </h2>
            <span className="text-sm text-gray-500">Max {TASK_WEIGHT} punten</span>
          </div>

          {tasks.length === 0 ? (
            <p className="text-gray-500 italic text-sm">Geen taken toegewezen</p>
          ) : (
            <div className="space-y-4">
              {tasks.map((task, index) => {
                const existingScore = existingTaskScores.find((s) => s.taskId === task.id);
                return (
                  <div key={task.id} className="border border-gray-200 rounded-lg p-4 print-card">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">
                          {index + 1}. {task.name}
                        </p>
                        {task.beschrijving && (
                          <p className="text-sm text-gray-500 mt-1">{task.beschrijving}</p>
                        )}
                      </div>
                      <span className="text-xs text-gray-400 ml-4">
                        max {pointsPerTask.toFixed(1)} pt
                      </span>
                    </div>

                    {/* Inline Score Criteria - Task-specific with fallback to general */}
                    <ContextAwareCriteriaBlock
                      contextType="task"
                      contextId={task.id}
                      generalCriteria={getSortedCriteria()}
                    />

                    {/* Score circles */}
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-sm text-gray-600 mr-2">Score:</span>
                      {[1, 2, 3, 4, 5].map((score) => (
                        <span
                          key={score}
                          className={`score-circle w-8 h-8 rounded-full border-2 flex items-center justify-center text-sm font-bold ${
                            existingScore?.score === score
                              ? 'filled bg-blue-600 border-blue-600 text-white'
                              : 'border-gray-300 text-gray-600'
                          }`}
                        >
                          {score}
                        </span>
                      ))}
                    </div>

                    {/* Agreement type checkboxes */}
                    {agreementTypes && (
                      <AgreementTypeCheckboxes
                        selectedTypeIds={existingScore?.selectedAgreementTypes || []}
                        agreementTypes={agreementTypes}
                        score={existingScore?.score ?? null}
                      />
                    )}

                    {/* Agreement description lines */}
                    <AgreementDescriptionLines
                      existingDescription={existingScore?.agreementDescription}
                    />

                    {/* Comment lines - more space for handwriting */}
                    <div>
                      <span className="text-sm text-gray-600">Toelichting:</span>
                      <div className="mt-2 space-y-6">
                        {existingScore?.toelichting ? (
                          <p className="text-sm text-gray-800 italic border-b border-gray-300 pb-1">{existingScore.toelichting}</p>
                        ) : (
                          <div className="border-b border-gray-300"></div>
                        )}
                        <div className="border-b border-gray-300"></div>
                        <div className="border-b border-gray-300"></div>
                        <div className="border-b border-gray-300"></div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        )}

        {/* Vak Competencies Section */}
        <div className="mb-8 print-section">
          <div className="flex items-center justify-between mb-3 print-section-header">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <span className="w-6 h-6 rounded bg-orange-600 text-white text-xs flex items-center justify-center">2</span>
              Vakcompetenties
            </h2>
            <span className="text-sm text-gray-500">Max {VAK_WEIGHT} punten</span>
          </div>

          {vakCompetencies.length === 0 ? (
            <p className="text-gray-500 italic text-sm">Geen vakcompetenties (via taken)</p>
          ) : (
            <div className="space-y-4">
              {vakCompetencies.map((comp, index) => {
                const existingScore = existingCompScores.find((s) => s.competencyId === comp.id);
                return (
                  <div key={comp.id} className="border border-gray-200 rounded-lg p-4 print-card">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">
                          {index + 1}. {comp.name}
                        </p>
                        {comp.beschrijving && (
                          <p className="text-sm text-gray-500 mt-1">{comp.beschrijving}</p>
                        )}
                      </div>
                      <span className="text-xs text-gray-400 ml-4">
                        max {pointsPerVak.toFixed(1)} pt
                      </span>
                    </div>

                    {/* Inline Score Criteria - Competency-specific with fallback to general */}
                    <ContextAwareCriteriaBlock
                      contextType="competency"
                      contextId={comp.id}
                      generalCriteria={getSortedCriteria()}
                    />

                    {/* Score circles */}
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-sm text-gray-600 mr-2">Score:</span>
                      {[1, 2, 3, 4, 5].map((score) => (
                        <span
                          key={score}
                          className={`score-circle w-8 h-8 rounded-full border-2 flex items-center justify-center text-sm font-bold ${
                            existingScore?.score === score
                              ? 'filled bg-orange-600 border-orange-600 text-white'
                              : 'border-gray-300 text-gray-600'
                          }`}
                        >
                          {score}
                        </span>
                      ))}
                    </div>

                    {/* Agreement type checkboxes */}
                    {agreementTypes && (
                      <AgreementTypeCheckboxes
                        selectedTypeIds={existingScore?.selectedAgreementTypes || []}
                        agreementTypes={agreementTypes}
                        score={existingScore?.score ?? null}
                      />
                    )}

                    {/* Agreement description lines */}
                    <AgreementDescriptionLines
                      existingDescription={existingScore?.agreementDescription}
                    />

                    {/* Comment lines - more space for handwriting */}
                    <div>
                      <span className="text-sm text-gray-600">Toelichting:</span>
                      <div className="mt-2 space-y-6">
                        {existingScore?.toelichting ? (
                          <p className="text-sm text-gray-800 italic border-b border-gray-300 pb-1">{existingScore.toelichting}</p>
                        ) : (
                          <div className="border-b border-gray-300"></div>
                        )}
                        <div className="border-b border-gray-300"></div>
                        <div className="border-b border-gray-300"></div>
                        <div className="border-b border-gray-300"></div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Basis Competencies Section */}
        <div className="mb-8 print-section">
          <div className="flex items-center justify-between mb-3 print-section-header">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <span className="w-6 h-6 rounded bg-purple-600 text-white text-xs flex items-center justify-center">3</span>
              Basiscompetenties
            </h2>
            <span className="text-sm text-gray-500">Max {BASIS_WEIGHT} punten</span>
          </div>

          {basisCompetencies.length === 0 ? (
            <p className="text-gray-500 italic text-sm">Geen basiscompetenties toegewezen</p>
          ) : (
            <div className="space-y-4">
              {basisCompetencies.map((comp, index) => {
                const existingScore = existingCompScores.find((s) => s.competencyId === comp.id);
                return (
                  <div key={comp.id} className="border border-gray-200 rounded-lg p-4 print-card">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">
                          {index + 1}. {comp.name}
                        </p>
                        {comp.beschrijving && (
                          <p className="text-sm text-gray-500 mt-1">{comp.beschrijving}</p>
                        )}
                      </div>
                      <span className="text-xs text-gray-400 ml-4">
                        max {pointsPerBasis.toFixed(1)} pt
                      </span>
                    </div>

                    {/* Inline Score Criteria - Competency-specific with fallback to general */}
                    <ContextAwareCriteriaBlock
                      contextType="competency"
                      contextId={comp.id}
                      generalCriteria={getSortedCriteria()}
                    />

                    {/* Score circles */}
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-sm text-gray-600 mr-2">Score:</span>
                      {[1, 2, 3, 4, 5].map((score) => (
                        <span
                          key={score}
                          className={`score-circle w-8 h-8 rounded-full border-2 flex items-center justify-center text-sm font-bold ${
                            existingScore?.score === score
                              ? 'filled bg-purple-600 border-purple-600 text-white'
                              : 'border-gray-300 text-gray-600'
                          }`}
                        >
                          {score}
                        </span>
                      ))}
                    </div>

                    {/* Agreement type checkboxes */}
                    {agreementTypes && (
                      <AgreementTypeCheckboxes
                        selectedTypeIds={existingScore?.selectedAgreementTypes || []}
                        agreementTypes={agreementTypes}
                        score={existingScore?.score ?? null}
                      />
                    )}

                    {/* Agreement description lines */}
                    <AgreementDescriptionLines
                      existingDescription={existingScore?.agreementDescription}
                    />

                    {/* Comment lines - more space for handwriting */}
                    <div>
                      <span className="text-sm text-gray-600">Toelichting:</span>
                      <div className="mt-2 space-y-6">
                        {existingScore?.toelichting ? (
                          <p className="text-sm text-gray-800 italic border-b border-gray-300 pb-1">{existingScore.toelichting}</p>
                        ) : (
                          <div className="border-b border-gray-300"></div>
                        )}
                        <div className="border-b border-gray-300"></div>
                        <div className="border-b border-gray-300"></div>
                        <div className="border-b border-gray-300"></div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Signatures */}
        <div className="border-t-2 border-gray-200 pt-6 mt-8">
          <div className="grid grid-cols-2 gap-8">
            <div>
              <p className="text-sm text-gray-600 mb-8">Handtekening Medewerker:</p>
              <div className="border-b border-gray-400 mb-2"></div>
              <p className="text-xs text-gray-500">Datum: _______________</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-8">Handtekening Beoordelaar:</p>
              <div className="border-b border-gray-400 mb-2"></div>
              <p className="text-xs text-gray-500">Datum: _______________</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 pt-4 border-t border-gray-200 text-center text-xs text-gray-400">
          <p>Functieloonhuis Beoordelingssysteem • Dit formulier is bedoeld voor offline gebruik</p>
        </div>
      </div>
    </>
  );
}
